"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, FilterX } from "lucide-react";

import { ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, Modal, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EmptyState } from "@/components/ds/empty-state";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { tableHeaderSearchInputClass, tableHeaderSelectTriggerClass, ThemedSelect, themedSelectMenuClass } from "@/components/ui-system/select";
import { exportReportResult, getReportResult, type ReportResultParams } from "@/lib/api/endpoints/report-results";
import { listDevicesPage } from "@/lib/api/endpoints/devices";
import { listIndianStates } from "@/lib/api/endpoints/organizations";
import { listUsers } from "@/lib/api/endpoints/users";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

export type ReportViewKey = "users" | "channels" | "sales-time" | "sales-region" | "warranty" | "alerts" | "support-tickets" | "data-usage";

type ReportConfig = {
  key: ReportViewKey;
  moduleKey: string;
  title: string;
  description: string;
  searchPlaceholder: string;
  showRange?: boolean;
  showWorkspace?: boolean;
  showDevice?: boolean;
  showState?: boolean;
  showAdmin?: boolean;
};

const REPORT_CONFIG: Record<ReportViewKey, ReportConfig> = {
  users: {
    key: "users",
    moduleKey: "reports.users",
    title: "Users report",
    description: "Workspace user listing with role and account status.",
    searchPlaceholder: "Search name, email, or role...",
    showWorkspace: true,
  },
  channels: {
    key: "channels",
    moduleKey: "reports.channels",
    title: "Channels Report",
    description: "Channel-wise telemetry coverage, last reading, and numeric aggregates.",
    searchPlaceholder: "Search workspace, data logger, channel, unit, or status...",
    showWorkspace: true,
    showRange: true,
    showDevice: true,
  },
  "sales-time": {
    key: "sales-time",
    moduleKey: "reports.sales.time",
    title: "Time based sale",
    description: "Data logger sales based on user assignment time.",
    searchPlaceholder: "Search data logger, UID, or assigned user...",
    showWorkspace: true,
    showRange: true,
    showDevice: true,
  },
  "sales-region": {
    key: "sales-region",
    moduleKey: "reports.sales.region",
    title: "Region based sale",
    description: "Sales grouped by data logger state or region.",
    searchPlaceholder: "Search region...",
    showWorkspace: true,
    showRange: true,
    showDevice: true,
    showState: true,
  },
  warranty: {
    key: "warranty",
    moduleKey: "reports.dataloggers.warranty",
    title: "Warranty report",
    description: "Data loggers filtered by warranty expiry date.",
    searchPlaceholder: "Search data logger or UID...",
    showWorkspace: true,
    showRange: true,
    showDevice: true,
  },
  alerts: {
    key: "alerts",
    moduleKey: "reports.alerts",
    title: "Alert report",
    description: "Triggered alert history by workspace, device, and time range.",
    searchPlaceholder: "Search alert message, severity, or device...",
    showWorkspace: true,
    showRange: true,
    showDevice: true,
  },
  "support-tickets": {
    key: "support-tickets",
    moduleKey: "reports.support.tickets",
    title: "Support ticket report",
    description: "Support tickets by workspace, requester, assignee, status, and time range.",
    searchPlaceholder: "Search subject, requester, assignee, status...",
    showWorkspace: true,
    showRange: true,
  },
  "data-usage": {
    key: "data-usage",
    moduleKey: "reports.data.usage",
    title: "Data usage report",
    description: "Admin storage usage across telemetry, alerts, and backup files.",
    searchPlaceholder: "Search admin, email, or status...",
    showAdmin: true,
  },
};

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "custom", label: "Custom range" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

const menuClassName = themedSelectMenuClass;
const optionClassName = "rounded-xl text-[13px]";

function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleString();
  }
  return String(value);
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function ReportResultsPage({ reportKey }: { reportKey: ReportViewKey }) {
  const config = REPORT_CONFIG[reportKey];
  const user = useAuthStore((state) => state.user);
  const tenantId = useAuthStore((state) => state.tenantId);
  const isSuperAdmin = user?.role === "super_admin";
  const { permissionMap } = useEffectivePermissions();
  const canExport = hasModulePermission(user, permissionMap, config.moduleKey, "export");

  const [search, setSearch] = useState("");
  const [rangeKey, setRangeKey] = useState(config.showRange ? "this_month" : "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [stateId, setStateId] = useState("");
  const [adminId, setAdminId] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(20);
  const [toast, setToast] = useState<string | null>(null);

  const effectiveTenantId = tenantId;
  const deviceFilterWorkspaceScope: "all" | null = isSuperAdmin && !effectiveTenantId ? "all" : null;
  const params = useMemo<ReportResultParams>(
    () => ({
      tenantId: effectiveTenantId,
      rangeKey: config.showRange ? rangeKey : null,
      startDate: rangeKey === "custom" ? startDate || null : null,
      endDate: rangeKey === "custom" ? endDate || null : null,
      search,
      deviceId: config.showDevice ? deviceId || null : null,
      stateId: config.showState ? stateId || null : null,
      adminId: config.showAdmin && isSuperAdmin ? adminId || null : null,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [adminId, config.showAdmin, config.showDevice, config.showRange, config.showState, deviceId, effectiveTenantId, endDate, isSuperAdmin, page, pageSize, rangeKey, search, startDate, stateId],
  );

  const query = useQuery({
    queryKey: ["report-results", reportKey, params],
    queryFn: () => getReportResult(reportKey, params),
  });

  const devicesQuery = useQuery({
    queryKey: ["report-device-filter", config.moduleKey, effectiveTenantId ?? "all", deviceFilterWorkspaceScope ?? "tenant"],
    queryFn: () =>
      listDevicesPage({
        tenantId: effectiveTenantId ?? undefined,
        workspaceScope: deviceFilterWorkspaceScope,
        moduleKey: config.moduleKey,
        limit: 200,
        offset: 0,
      }),
    enabled: Boolean(config.showDevice && (isSuperAdmin || effectiveTenantId)),
  });

  const statesQuery = useQuery({
    queryKey: ["indian-states", "report-filter"],
    queryFn: listIndianStates,
    enabled: Boolean(config.showState),
  });

  const adminsQuery = useQuery({
    queryKey: ["report-admin-filter", "all-workspaces"],
    queryFn: () => listUsers(null, "all"),
    enabled: Boolean(config.showAdmin && isSuperAdmin),
    staleTime: 60_000,
  });

  const adminOptions = useMemo(
    () =>
      (adminsQuery.data?.items ?? [])
        .filter((item) => item.role === "admin")
        .map((item) => ({
          value: item.id,
          label: item.name || item.email,
          description: item.email,
        })),
    [adminsQuery.data?.items],
  );

  async function handleExport() {
    if (!canExport) {
      setToast("You don't have permission to export this report.");
      return;
    }
    const blob = await exportReportResult(reportKey, params);
    triggerBlobDownload(blob, `${reportKey}-report.csv`);
  }

  function openCustomRangeModal() {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setIsCustomRangeModalOpen(true);
  }

  useEffect(() => {
    setPage(0);
  }, [adminId, deviceId, effectiveTenantId, endDate, rangeKey, reportKey, search, startDate, stateId]);

  const hasFilters = Boolean(search.trim() || deviceId || stateId || adminId || (config.showRange && rangeKey !== "this_month"));
  const total = query.data?.total ?? 0;
  const displayColumns = useMemo(
    () =>
      isSuperAdmin
        ? query.data?.columns ?? []
        : (query.data?.columns ?? []).filter((column) => column.key !== "workspace"),
    [isSuperAdmin, query.data?.columns],
  );
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canGoPrevious = page > 0;
  const canGoNext = Boolean(query.data?.next_offset);
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + (query.data?.rows.length ?? pageSize));
  const pageLabel = total ? `${from}-${to} of ${total}` : "0 of 0";

  return (
    <EnterprisePageShell hideHeader title={config.title} description={config.description}>
      <div className="grid gap-5 p-5">
        {toast ? (
          <div className="rounded-lg border border-card-border bg-card px-4 py-3 text-sm text-foreground">{toast}</div>
        ) : null}
        <ListSurface>
          <ListSurfaceHeader
            title={query.data?.title ?? config.title}
            meta={<ListCount total={total} noun="rows" />}
            controls={
              <ListToolbar>
                <ListToolbarGroup grow>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={config.searchPlaceholder}
                    className={tableHeaderSearchInputClass}
                  />
                </ListToolbarGroup>
                {config.showAdmin && isSuperAdmin ? (
                  <ListToolbarGroup>
                    <ThemedSelect
                      value={adminId}
                      onChange={setAdminId}
                      options={[{ value: "", label: "All admins" }, ...adminOptions]}
                      ariaLabel="Admin filter"
                      searchable
                      className="min-w-[8rem]"
                      triggerClassName={tableHeaderSelectTriggerClass}
                      menuClassName={menuClassName}
                      optionClassName={optionClassName}
                    />
                  </ListToolbarGroup>
                ) : null}
                {config.showRange ? (
                  <ListToolbarGroup>
                    <ThemedSelect
                      value={rangeKey}
                      onChange={(value) => {
                        if (value === "custom") {
                          openCustomRangeModal();
                          return;
                        }
                        setRangeKey(value);
                      }}
                      options={RANGE_OPTIONS}
                      ariaLabel="Date range"
                      className="min-w-[8rem]"
                      triggerClassName={tableHeaderSelectTriggerClass}
                      menuClassName={menuClassName}
                      optionClassName={optionClassName}
                    />
                  </ListToolbarGroup>
                ) : null}
                {config.showState ? (
                  <ListToolbarGroup>
                    <ThemedSelect
                      value={stateId}
                      onChange={setStateId}
                      options={[
                        { value: "", label: "All regions" },
                        ...(statesQuery.data ?? []).map((state) => ({ value: state.id, label: state.name, description: state.code })),
                      ]}
                      ariaLabel="Region filter"
                      searchable
                      className="min-w-[8rem]"
                      triggerClassName={tableHeaderSelectTriggerClass}
                      menuClassName={menuClassName}
                      optionClassName={optionClassName}
                    />
                  </ListToolbarGroup>
                ) : null}
                {config.showDevice ? (
                  <ListToolbarGroup>
                    <ThemedSelect
                      value={deviceId}
                      onChange={setDeviceId}
                      options={[
                        { value: "", label: "All loggers" },
                        ...(devicesQuery.data?.items ?? []).map((device) => ({
                          value: device.id,
                          label: device.name || device.device_uid,
                          description: device.device_uid,
                        })),
                      ]}
                      ariaLabel="Device filter"
                      searchable
                      className="min-w-[8rem]"
                      triggerClassName={tableHeaderSelectTriggerClass}
                      menuClassName={menuClassName}
                      optionClassName={optionClassName}
                    />
                  </ListToolbarGroup>
                ) : null}
                {hasFilters ? (
                  <ToolbarIconButton
                    icon={FilterX}
                    label="Clear filters"
                    onClick={() => {
                      setSearch("");
                      setDeviceId("");
                      setStateId("");
                      setAdminId("");
                      setRangeKey(config.showRange ? "this_month" : "");
                      setStartDate("");
                      setEndDate("");
                      setDraftStartDate("");
                      setDraftEndDate("");
                    }}
                  />
                ) : null}
                {canExport ? (
                  <ToolbarIconButton icon={Download} label="Export CSV" variant="primary" onClick={handleExport} />
                ) : null}
              </ListToolbar>
            }
          />
          <ListSurfaceBody className="overflow-x-auto">
            {query.isLoading ? (
              <div className="p-ds-4" aria-label="Loading report rows">
                {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
              </div>
            ) : query.isError ? (
              <div className="p-4 text-sm text-red-700">
                {query.error instanceof Error ? query.error.message : "Failed to load report."}
              </div>
            ) : query.data?.rows.length ? (
              <table className="min-w-full border-collapse">
                <thead className="bg-[var(--bg-surface)]">
                  <tr>
                    {displayColumns.map((column) => (
                      <th key={column.key} className="border-b border-card-border px-4 py-3 text-left text-[11px] font-bold uppercase text-muted">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {query.data.rows.map((row, index) => (
                    <tr key={index} className={index % 2 === 1 ? "bg-primary/5" : "bg-card"}>
                      {displayColumns.map((column) => (
                        <td key={column.key} className="border-b border-card-border px-4 py-3 text-sm text-foreground">
                          {formatCell(row[column.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6">
                <EmptyState title="No report rows found" description="Adjust filters or date range to find matching records." />
              </div>
            )}
          </ListSurfaceBody>
          <ListSurfaceFooter
            left={
              <div className="flex flex-wrap items-center gap-ds-3">
                <label className="flex items-center gap-ds-2">
                  <span className="text-caption text-muted">Rows per page</span>
                  <ThemedSelect
                    value={String(pageSize)}
                    ariaLabel="Rows per page"
                    className="w-[5.5rem]"
                    triggerClassName={tableHeaderSelectTriggerClass}
                    options={PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
                    onChange={(nextValue) => {
                      setPage(0);
                      setPageSize(Number(nextValue) as (typeof PAGE_SIZE_OPTIONS)[number]);
                    }}
                  />
                </label>
                <span className="text-caption text-muted">Report results {pageLabel}</span>
              </div>
            }
            right={
              <>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                  disabled={!canGoPrevious}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  Previous
                </button>
                <span className="text-caption tabular-nums text-muted">
                  Page {Math.min(page + 1, pageCount)} / {pageCount}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                  disabled={!canGoNext}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </>
            }
          />
        </ListSurface>
      </div>
      <Modal
        open={isCustomRangeModalOpen}
        onClose={() => setIsCustomRangeModalOpen(false)}
        title="Custom report range"
        description={`Choose the date window used for ${config.title.toLowerCase()}.`}
        footer={
          <div className="flex justify-end gap-ds-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setIsCustomRangeModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setStartDate(draftStartDate);
                setEndDate(draftEndDate);
                setRangeKey("custom");
                setIsCustomRangeModalOpen(false);
              }}
              disabled={!draftStartDate || !draftEndDate}
            >
              Apply range
            </button>
          </div>
        }
      >
        <div className="grid gap-ds-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">Start</span>
            <input
              type="date"
              value={draftStartDate}
              onChange={(event) => setDraftStartDate(event.target.value)}
              className="h-11 rounded-lg border border-border bg-card px-4 text-caption text-foreground shadow-sm outline-none transition-colors focus:border-primary"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">End</span>
            <input
              type="date"
              value={draftEndDate}
              onChange={(event) => setDraftEndDate(event.target.value)}
              className="h-11 rounded-lg border border-border bg-card px-4 text-caption text-foreground shadow-sm outline-none transition-colors focus:border-primary"
            />
          </label>
        </div>
      </Modal>
    </EnterprisePageShell>
  );
}
